import { c as _c } from "react/compiler-runtime";
// Блок «Доставка» на карточке товара: как ЭТОТ продавец доставит товар в
// город покупателя, сколько это стоит и когда привезут. Те же варианты, что
// покажет оформление заказа: СДЭК (пункт выдачи или постамат, курьер) либо
// тариф площадки и «Доставка продавцом», если продавец возит сам в этот город
// (цена, срок, «бесплатно от», условия продавца — из кабинета продавца).
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, type CitySuggestion, type DeliveryOption } from "@/api";
import { MarkerPin20Icon } from "@/components/icons";
import { Input } from "@/components/ui/text-field";
import { DEFAULT_DELIVERY_CITY, readDeliveryCity, saveDeliveryCity } from "@/lib/delivery-city";
import { carrierName } from "@/lib/delivery-labels";
import { SELF_DELIVERY_LABEL } from "@/lib/self-delivery";
import { SELLER_PICKUP_LABEL } from "@/lib/seller-pickup";
import { useFormatPrice } from "@/lib/use-format-price";
import { useProfile } from "@/store";

// Подпись варианта квоты для покупателя: у СДЭК «пункт выдачи» — это и
// постаматы, говорим об этом прямо.
const optionTitle = (carrier: string, o: DeliveryOption): string => {
  if (carrier === "sdek") return o.toPickupPoint ? "СДЭК: пункт выдачи или постамат" : "СДЭК: курьером до двери";
  return o.label;
};
export function ProductDelivery(t0) {
  const $ = _c(36);
  const {
    productId,
    variantId
  } = t0;
  const fmt = useFormatPrice();
  const token = useProfile(_temp);
  const [city, setCity] = useState(_temp2);
  const [editing, setEditing] = useState(false);
  const [q, setQ] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [suggest, setSuggest] = useState(t1);
  let t2;
  let t3;
  if ($[1] !== city || $[2] !== token) {
    t2 = () => {
      if (city) {
        return;
      }
      if (!token) {
        setCity(DEFAULT_DELIVERY_CITY);
        return;
      }
      api.buyer.listAddresses().then(list => setCity((list.find(_temp3) ?? list[0])?.city || DEFAULT_DELIVERY_CITY)).catch(() => setCity(DEFAULT_DELIVERY_CITY));
    };
    t3 = [city, token];
    $[1] = city;
    $[2] = token;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== editing || $[6] !== q) {
    t4 = () => {
      const query = q.trim();
      if (!editing || query.length < 2) {
        setSuggest([]);
        return;
      }
      const t = setTimeout(() => {
        api.geo.citySuggest(query).then(r => setSuggest(r.slice(0, 6))).catch(() => setSuggest([]));
      }, 250);
      return () => clearTimeout(t);
    };
    t5 = [q, editing];
    $[5] = editing;
    $[6] = q;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  const t6 = variantId ?? "";
  let t7;
  if ($[9] !== city || $[10] !== productId || $[11] !== t6) {
    t7 = ["delivery", "product", productId, city, t6];
    $[9] = city;
    $[10] = productId;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== city || $[14] !== productId || $[15] !== variantId) {
    t8 = () => api.delivery.productOptions({
      productId,
      city,
      variantId
    });
    $[13] = city;
    $[14] = productId;
    $[15] = variantId;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = Boolean(city);
  let t10;
  if ($[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = {
      queryKey: t7,
      queryFn: t8,
      enabled: t9,
      retry: false,
      staleTime: 300000
    };
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const info = useQuery(t10);
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = next => {
      const value = next.trim();
      setEditing(false);
      if (!value) {
        return;
      }
      setCity(value);
      saveDeliveryCity(value);
    };
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const pick = t11;
  const data = info.data;
  let t12;
  if ($[22] !== city || $[23] !== data || $[24] !== editing || $[25] !== fmt || $[26] !== info.isError || $[27] !== info.isLoading || $[28] !== q || $[29] !== suggest) {
    const rows = [];
    if (data) {
      for (const o of data.options) {
        rows.push({
          key: `${o.carrier}:${o.tariffCode ?? o.method ?? ""}`,
          title: optionTitle(data.carrier, o),
          sub: o.deliveryDays,
          price: o.unavailable ? undefined : o.deliverySum,
          muted: o.unavailable ? o.unavailableReason || "\u041D\u0435\u0434\u043E\u0441\u0442\u0443\u043F\u043D\u043E \u0434\u043B\u044F \u044D\u0442\u043E\u0433\u043E \u0433\u043E\u0440\u043E\u0434\u0430" : undefined
        });
      }
      if (data.self) {
        const s_0 = data.self;
        const free = s_0.freeOver != null && s_0.freeOver > 0 ? `бесплатно от ${fmt(s_0.freeOver)}` : undefined;
        rows.push({
          key: "self",
          title: SELF_DELIVERY_LABEL,
          sub: [s_0.deliveryDays, free].filter(Boolean).join(" \xB7 "),
          price: s_0.price
        });
      }
      if (data.sellerPickup && data.sellerPickup.cities.length > 0) {
        rows.push({
          key: "seller-pickup",
          title: SELLER_PICKUP_LABEL,
          sub: data.sellerPickup.cities.map(_temp4).join(", "),
          price: data.sellerPickup.minPrice,
          pricePrefix: data.sellerPickup.minPrice > 0 ? "\u043E\u0442 " : undefined
        });
      }
    }
    let t13;
    if ($[31] !== city || $[32] !== editing || $[33] !== q || $[34] !== suggest) {
      t13 = <div className="flex flex-wrap items-center gap-2">{editing ? <div className="relative"><Input autoFocus={true} value={q} placeholder={"\u0413\u043E\u0440\u043E\u0434"} data-testid="product-delivery-city-input" onChange={e => setQ(e.target.value)} onKeyDown={e_0 => {
            if (e_0.key === "Enter") {
              pick(q);
            }
            if (e_0.key === "Escape") {
              setEditing(false);
            }
          }} onBlur={() => setTimeout(() => setEditing(false), 150)} fieldClassName="w-56" />{suggest.length > 0 && <ul className="absolute top-full left-0 z-10 mt-1 max-h-60 w-64 overflow-auto rounded-[10px] bg-white p-1 shadow-[0_4px_24px_rgba(0,0,0,0.12)]">{suggest.map(c_0 => <li key={c_0.value}><button type="button" onMouseDown={_temp5} onClick={() => pick(c_0.city)} className="w-full rounded-[8px] px-3 py-2 text-left text-[14px] text-[#1F1F1F] hover:bg-[#F8F8F8]">{c_0.value}</button></li>)}</ul>}</div> : <button type="button" data-testid="product-delivery-city" onClick={() => {
          setQ("");
          setEditing(true);
        }} title={"\u0421\u043C\u0435\u043D\u0438\u0442\u044C \u0433\u043E\u0440\u043E\u0434"} className="flex items-center gap-1.5 rounded-sm text-[14px] font-semibold leading-[18px] text-[#2E2E2E] outline-none transition-colors hover:text-[#FA5C2E] focus-visible:ring-2 focus-visible:ring-ds-focus"><MarkerPin20Icon />{city || "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0433\u043E\u0440\u043E\u0434"}</button>}</div>;
      $[31] = city;
      $[32] = editing;
      $[33] = q;
      $[34] = suggest;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    t12 = <section className="flex flex-col gap-3" data-testid="product-delivery">{t13}{info.isLoading ? <p className="text-[14px] leading-[18px] text-[#1F1F1F]/48">Считаем доставку…</p> : info.isError || !data ? <p className="text-[14px] leading-[18px] text-[#1F1F1F]/48">Стоимость и срок покажем при оформлении заказа.</p> : <><dl className="grid grid-cols-[160px_minmax(0,1fr)] gap-x-4 gap-y-3 text-[14px] leading-[18px] text-[#2E2E2E]">{rows.map(r_0 => <div key={r_0.key} className="contents" data-testid={r_0.key === "self" ? "product-delivery-self" : r_0.key === "seller-pickup" ? "product-delivery-seller-pickup" : "product-delivery-option"}><dt className={r_0.muted ? "font-semibold text-[#2E2E2E]/48" : "font-semibold"}>{r_0.title}</dt><dd className="min-w-0 font-medium">{r_0.muted ? <span className="text-[#2E2E2E]/48">{r_0.muted}</span> : <>{r_0.price != null && (r_0.price === 0 ? "\u0431\u0435\u0441\u043F\u043B\u0430\u0442\u043D\u043E" : `${r_0.pricePrefix ?? ""}${fmt(r_0.price)}`)}{r_0.sub && <span className="ml-2 text-[#1F1F1F]/48">{r_0.sub}</span>}</>}</dd></div>)}</dl>{data.self?.note?.trim() ? <p className="text-[14px] leading-[18px] text-[#1F1F1F]">Условия продавца: {data.self.note.trim()}</p> : null}{data.carrier === "flat" && <p className="text-[14px] leading-[18px] text-[#1F1F1F]">{carrierName("flat")} — итоговая цена при оформлении, зависит от суммы заказа.</p>}</>}</section>;
    $[22] = city;
    $[23] = data;
    $[24] = editing;
    $[25] = fmt;
    $[26] = info.isError;
    $[27] = info.isLoading;
    $[28] = q;
    $[29] = suggest;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
}
function _temp5(e_1) {
  return e_1.preventDefault();
}
function _temp4(c) {
  return `г. ${c}`;
}
function _temp3(a) {
  return a.isDefault;
}
function _temp2() {
  return readDeliveryCity() ?? "";
}
function _temp(s) {
  return s.token;
}